import { AffidavitReconciliationSettings } from '../../types/organization';
import {
  NoticeAddOnPricingConfig,
  NoticeAddOnPricingContext,
  resolveNoticeAddOnPricingConfig
} from './types';
import { deriveNoticeAddOnPricingContext } from './shared';

/**
 * What the filer was quoted for add-ons, frozen when they were shown it.
 *
 * Pricing must not re-derive these from live configuration: a publisher moving
 * between wet signature and RON, or repricing their add-ons, would otherwise
 * change what an already-placed notice bills. Only the inputs that determined the
 * quote are captured — the operational half of the ARS (affidavit template,
 * upload method, reconciliation start date) is deliberately absent and continues
 * to follow the publisher.
 */
export type NoticeAddOnQuote = {
  /** Resolved without the notice-level override; see `AffidavitPricingData.paperLevelSettings`. */
  affidavitsManagedByColumn: boolean;
  notarizationVendor?: AffidavitReconciliationSettings['notarizationVendor'];
  /** Every add-on fee as quoted, resolved so a later default change cannot move it. */
  noticeAddOnPricing: Required<NoticeAddOnPricingConfig>;
};

export const buildNoticeAddOnQuote = (
  paperLevelSettings:
    | Pick<
        AffidavitReconciliationSettings,
        'affidavitsManagedByColumn' | 'notarizationVendor'
      >
    | null
    | undefined,
  noticeAddOnPricing: NoticeAddOnPricingConfig | undefined
): NoticeAddOnQuote => ({
  affidavitsManagedByColumn:
    paperLevelSettings?.affidavitsManagedByColumn ?? false,
  // Omitted rather than left undefined: the quote is written straight onto the
  // notice, and Firestore rejects an undefined value anywhere in the document.
  ...(paperLevelSettings?.notarizationVendor
    ? { notarizationVendor: paperLevelSettings.notarizationVendor }
    : {}),
  noticeAddOnPricing: resolveNoticeAddOnPricingConfig(noticeAddOnPricing)
});

export const getContextFromNoticeAddOnQuote = (
  quote: NoticeAddOnQuote
): NoticeAddOnPricingContext =>
  deriveNoticeAddOnPricingContext({
    affidavitsManagedByColumn: quote.affidavitsManagedByColumn,
    notarizationVendor: quote.notarizationVendor
  });
